import useI18n from '@/hooks/useI18n';
import modulePageTextSetting from '@/modulePageTextSetting';
import { commonFormValidate } from '@/utils/utils';
import { useModel } from '@umijs/max';
import { useRequest } from 'ahooks';
import { Col, Divider, Form, Input, Row, Table, Typography } from 'antd';
import {
  Ref,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import memberCommon from '../../memberCommon';
import { queryOfferService } from '../../services';
import SelectMsisdn from './components/SelectMsisdn';
import SelectOfferPopEdit from './components/SelectOfferPopEdit';

const NewConnection = ({}: any, ref: Ref<unknown> | undefined) => {
  const [form] = Form.useForm();
  const msisdnRef: any = useRef();
  const offerRef: any = useRef();
  const { formatMessage } = useI18n();
  const { setSubmitBtnDisabled, newConnection, selectedAccount } = useModel(
    'MemberMgr.memberModel',
  );
  const { initialState } = useModel('@@initialState');
  const { custId, bcareUserId } = initialState || {};
  const { currencyTypeData = 'RM' } = useModel('global');
  const [mainOfferId, setMainOfferId] = useState<any>(undefined);
  const [selectAddons, seSelectAddons] = useState<any>([]);
  const { useBatchSuccessModal } = memberCommon;
  const { commonBatchSuccessModal } = useBatchSuccessModal();

  const { data, loading }: any = useRequest(
    () => {
      return (
        mainOfferId &&
        queryOfferService({
          sourceProductOfferingIds: [mainOfferId],
          sourceProductOfferingType: [1],
          relationship: '0',
          productOfferingType: [2],
          channel: '18',
          marketSegment: [{ segmentType: 1 }],
          custId,
          subsEventId: '1',
        })
      );
    },
    {
      refreshDeps: [mainOfferId],
    },
  );

  const columns = [
    {
      title: formatMessage('memberMgr.name'),
      dataIndex: 'name',
      key: 'name',
      width: 300,
      ellipsis: true,
    },
    {
      title: formatMessage('memberMgr.salePrice', {
        currencySymbol: currencyTypeData,
      }),
      dataIndex: 'saleListPrice',
      key: 'saleListPrice',
      ellipsis: true,
    },
    {
      title: formatMessage('memberMgr.rentPrice', {
        currencySymbol: currencyTypeData,
      }),
      dataIndex: 'rentListPrice',
      key: 'rentListPrice',
      ellipsis: true,
    },
  ];

  useEffect(() => {
    setSubmitBtnDisabled(false);
  }, []);

  const onSelectChange = (
    selectedRowKeys: React.Key[],
    selectedRows: any[],
  ) => {
    seSelectAddons(selectedRows);
  };

  const onSubmit = async () => {
    const formValues = await form.validateFields();
    const params = {
      custId,
      acctId: selectedAccount?.acctId,
      orderExtInfo: formValues.referenceCode
        ? {
            attrs: [
              {
                attrCode: 'UM_ORDER_ON_BEHALF_FLAG',
                attrValue: 'Y',
              },
              {
                attrCode: 'UM_ORDER_ON_BEHALF_ORG_CODE',
                attrValue: formValues.referenceCode,
              },
            ],
          }
        : {},
      partyType: 'Y',
      partyCode: bcareUserId.toString(),
      offerCode: formValues.newOfferName.code,
      iccid: formValues.iccid,
      msisdn: formValues.newMsisdn.newMsisdn,
      addons: selectAddons.map((el: any) => ({
        offerCode: el.code,
        operationType: 'A',
        attrs: el.attrs,
      })),
    };
    const { custOrderId } = await newConnection(params);
    // batchSuccessModal(
    //   `${formatMessage('memberMgr.commonSuccess')}${custOrderId}`,
    // );
    commonBatchSuccessModal(custOrderId);
    return true;
  };

  useImperativeHandle(ref, () => ({
    onSubmit,
  }));

  const queryOfferParams = {
    productOfferingType: [1],
    channel: 18,
    marketSegment: [{ segmentType: 1 }],
    isBundleFlag: 'N',
    custId,
  };

  const onSelectOffer = () => {
    const offer = offerRef.current.selectedOffer;
    setMainOfferId(offer.id);
  };

  return (
    <>
      <Form form={form} colon={false} layout="vertical">
        <Row gutter={[24, 0]}>
          <Col span={12}>
            <Form.Item
              label={formatMessage('memberMgr.newSelectMsisdn')}
              name="newMsisdn"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <SelectMsisdn model="newConnection" ref={msisdnRef} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={formatMessage('memberMgr.simSerialNumber')}
              name="iccid"
              rules={[
                {
                  required: true,
                  pattern: /^[0-9]*$/g,
                  message: formatMessage('common.must.be.number'),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ marginTop: 0 }} />
        <Row gutter={[24, 0]}>
          <Col span={12}>
            <Form.Item
              label={formatMessage('memberMgr.newSelectOffer')}
              name="newOfferName"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <SelectOfferPopEdit
                ref={offerRef}
                queryParams={queryOfferParams}
                onSelectChange={onSelectOffer}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={formatMessage('memberMgr.referenceCode')}
              name="referenceCode"
              rules={[
                {
                  validator(_, value) {
                    const { maxLength } =
                      modulePageTextSetting.memberMgr_NewConnection_referenceCode;
                    return commonFormValidate(value, maxLength, formatMessage);
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Typography.Title level={5}>
        {formatMessage('memberMgr.newSelectAddOn')}
      </Typography.Title>
      <Table
        dataSource={data?.productOffering || []}
        pagination={false}
        rowKey="id"
        columns={columns}
        scroll={{ y: '50%' }}
        rowSelection={{
          onChange: onSelectChange,
          type: 'radio',
        }}
        loading={loading}
      />
    </>
  );
};

export default forwardRef(NewConnection);
